import { onboardingStateStorageKey } from 'pkg/onboarding_storage';

const IOS_APP_ID = '1440982399';

const registrationPathPrefix = '/registration/';

function getAppleMetaTag(): HTMLElement | null {
	return document.getElementById('ios-app-meta');
}

export function setAppleMeta(url: string): void {
	const appleMetaTag = getAppleMetaTag();

	if (url.startsWith('/')) {
		url = url.substring(1, url.length);
	}

	if (appleMetaTag) {
		appleMetaTag.setAttribute(
			'content',
			`app-id=${IOS_APP_ID}, app-argument=threesixty://${url}`
		);
	}
}

/**
 * Hides the iOS Smart App Banner. iOS only renders the banner when the
 * apple-itunes-app tag carries an app-id, so clearing the content attribute
 * suppresses it while leaving the tag in place for setAppleMeta to restore.
 */
export function removeAppleMeta(): void {
	getAppleMetaTag()?.setAttribute('content', '');
}

/**
 * The iOS Smart App Banner is suppressed throughout the registration flow.
 *
 * REG-989: iOS does not carry the banner's app-argument through an App Store
 * install, so a user who taps Download mid-registration lands on the app's home
 * screen with no reference to the registration they were signing up for. We
 * remove the install path rather than making deferred deep linking work — if
 * that is implemented later (Branch/AppsFlyer or a claim endpoint), this
 * suppression can be reverted.
 *
 * The condition is wider than the URL suggests: the sign-in step sits mid-flow
 * at /login, outside the /registration/ prefix, reached with returnToForms set
 * in the onboarding state (see routes/public/steps/login/Login.tsx).
 */
function shouldSuppressAppleMeta(location: string): boolean {
	const path = location.split('?')[0].split('#')[0];

	if (path.startsWith(registrationPathPrefix)) {
		return true;
	}

	// Read session storage directly rather than through useOnboardingState —
	// this runs pre-render and outside the Onboarding.Wrapper tree.
	try {
		const stored = sessionStorage.getItem(onboardingStateStorageKey);

		return stored ? JSON.parse(stored).returnToForms === true : false;
	} catch {
		return false;
	}
}

/**
 * Points the iOS Smart App Banner's meta tag at the given location, or clears it
 * when the banner should be suppressed. See shouldSuppressAppleMeta.
 */
export function syncAppleMeta(location: string): void {
	if (shouldSuppressAppleMeta(location)) {
		removeAppleMeta();
	} else {
		setAppleMeta(location);
	}
}

export function setThemeColor(color: string): void {
	const themeTag = document.getElementById('theme-color-meta');

	if (!themeTag) {
		return;
	}
	themeTag.setAttribute('content', color);
}
